// skipcq: JS-W1028, JS-0128
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Rating,
  Stack,
  Grid,
} from '@mui/material';

import ReviewReactionDock from './ReviewReactionDock';
import ReviewPhotosStrip from './ReviewPhotosStrip';
import ReviewDetailsNewChip from '../reviewDetails/ReviewDetailsNewChip';
import { formatReviewDateLine } from '../../utils/formatReviewDateLine';

const HOSPITALITY_RATING_DIMENSIONS = [
  { key: 'atmosphere', label: 'Atmosphere' },
  { key: 'food', label: 'Food' },
  { key: 'quality', label: 'Quality' },
  { key: 'service', label: 'Service' },
];

function ProfileHospitalityRatings({ ratings }) {
  const panels = HOSPITALITY_RATING_DIMENSIONS.map((dimension) => {
    const ratingValue = ratings?.[dimension.key];
    if (ratingValue === undefined || ratingValue <= 0) {
      return null;
    }
    return (
      <Box key={dimension.key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{ minWidth: 100 }}>
          {dimension.label}
        </Typography>
        <Rating value={ratingValue} max={5} readOnly size="small" />
      </Box>
    );
  }).filter(Boolean);

  if (panels.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        px: { xs: 1.5, sm: 2 },
        py: 1,
        bgcolor: 'background.paper',
      }}
    >
      <Stack spacing={1.5}>{panels}</Stack>
    </Box>
  );
}

ProfileHospitalityRatings.propTypes = {
  ratings: PropTypes.shape({
    atmosphere: PropTypes.number,
    food: PropTypes.number,
    quality: PropTypes.number,
    service: PropTypes.number,
  }),
};

function FullHospitalityRatings({ ratings }) {
  if (!ratings || Object.keys(ratings).length === 0) {
    return null;
  }

  return (
    <Box>
      <Stack spacing={1.5}>
        {HOSPITALITY_RATING_DIMENSIONS.map((dimension) => {
          const ratingValue = ratings[dimension.key];
          if (ratingValue === undefined || ratingValue <= 0) {
            return null;
          }
          return (
            <Box key={dimension.key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ minWidth: 100 }}>
                {dimension.label}
              </Typography>
              <Rating value={ratingValue} max={5} readOnly size="small" />
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

FullHospitalityRatings.propTypes = {
  ratings: PropTypes.shape({
    atmosphere: PropTypes.number,
    food: PropTypes.number,
    quality: PropTypes.number,
    service: PropTypes.number,
  }),
};

function HospitalityReviewUserMeta({
  userName, date, editedAt, experienceDate, comment,
}) {
  return (
    <>
      <Typography variant="body2" gutterBottom sx={{ color: 'text.secondary', mb: 0 }}>
        User: {userName}
      </Typography>
      <Typography variant="body2" gutterBottom sx={{ color: 'text.secondary' }}>
        Date: {formatReviewDateLine(date, editedAt, experienceDate)}
      </Typography>
      {comment ? (
        <Typography
          variant="body2"
          sx={{
            color: 'text.primary',
            mt: 2,
            whiteSpace: 'pre-wrap',
          }}
        >
          {comment}
        </Typography>
      ) : null}
    </>
  );
}

HospitalityReviewUserMeta.propTypes = {
  userName: PropTypes.string.isRequired,
  date: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]).isRequired,
  editedAt: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  experienceDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  comment: PropTypes.string,
};

function ReviewDetailsProfileLayout({
  review,
  breweryId,
  showBreweryReactionDock,
  showNewChip,
  analyticsSource,
  itemType,
}) {
  const { userName, date, comment, ratings } = review;

  return (
    <Box sx={{ position: 'relative' }}>
      {showNewChip ? (
        <Box sx={{ position: 'absolute', top: 0, right: 0, zIndex: 1 }}>
          <ReviewDetailsNewChip />
        </Box>
      ) : null}
      <Stack spacing={2} sx={{ pt: showNewChip ? 3 : 0 }}>
        <HospitalityReviewUserMeta
          userName={userName}
          date={date}
          editedAt={review.editedAt}
          experienceDate={review.experienceDate}
          comment={comment}
        />
        <ReviewPhotosStrip
          reviewType="hospitality"
          reviewId={review.id}
          photoIds={review.photoIds}
          photoSummary={review.photoSummary}
        />
        <ProfileHospitalityRatings ratings={ratings} />
        {showBreweryReactionDock && breweryId ? (
          <ReviewReactionDock
            breweryId={breweryId}
            review={review}
            reviewType="hospitality"
            enabled
            analyticsSource={analyticsSource}
            itemType={itemType}
          />
        ) : null}
      </Stack>
    </Box>
  );
}

ReviewDetailsProfileLayout.propTypes = {
  review: PropTypes.shape({
    id: PropTypes.string,
    userName: PropTypes.string.isRequired,
    date: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]).isRequired,
    editedAt: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    experienceDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    comment: PropTypes.string,
    ratings: PropTypes.object,
    photoIds: PropTypes.arrayOf(PropTypes.string),
    photoSummary: PropTypes.object,
  }).isRequired,
  breweryId: PropTypes.string,
  showBreweryReactionDock: PropTypes.bool,
  showNewChip: PropTypes.bool,
  analyticsSource: PropTypes.oneOf(['pint-portfolio', 'review-details']),
  itemType: PropTypes.oneOf(['beer', 'brewery']),
};

/**
 * HospitalityReviewCard component to display a single hospitality review
 */
function HospitalityReviewCard({
  review,
  variant = 'full',
  breweryId,
  showBreweryReactionDock = false,
  showNewChip = false,
  analyticsSource = 'review-details',
  itemType = 'brewery',
}) {
  const { breweryName, userName, date, comment, ratings } = review;
  const isProfileVariant = variant === 'profile';
  const isReviewDetailsLayout = isProfileVariant && analyticsSource === 'review-details';

  return (
    <Card
      sx={{
        width: '100%',
        maxWidth: 600,
        mb: 2,
        mx: isProfileVariant ? 'auto' : undefined,
      }}
    >
      <CardContent>
        {isReviewDetailsLayout ? (
          <ReviewDetailsProfileLayout
            review={review}
            breweryId={breweryId}
            showBreweryReactionDock={showBreweryReactionDock}
            showNewChip={showNewChip}
            analyticsSource={analyticsSource}
            itemType={itemType}
          />
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              {variant === 'full' ? (
                <Typography variant="h6" component="div" gutterBottom>
                  {breweryName}
                </Typography>
              ) : null}
              <HospitalityReviewUserMeta
                userName={userName}
                date={date}
                editedAt={review.editedAt}
                experienceDate={review.experienceDate}
                comment={comment}
              />
              <ReviewPhotosStrip
                reviewType="hospitality"
                reviewId={review.id}
                photoIds={review.photoIds}
                photoSummary={review.photoSummary}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              {isProfileVariant ? (
                <ProfileHospitalityRatings ratings={ratings} />
              ) : (
                <FullHospitalityRatings ratings={ratings} />
              )}
              {isProfileVariant && showBreweryReactionDock && breweryId ? (
                <ReviewReactionDock
                  breweryId={breweryId}
                  review={review}
                  reviewType="hospitality"
                  enabled
                  analyticsSource={analyticsSource}
                  itemType={itemType}
                />
              ) : null}
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
}

HospitalityReviewCard.propTypes = {
  review: PropTypes.shape({
    id: PropTypes.string,
    breweryName: PropTypes.string.isRequired,
    userName: PropTypes.string.isRequired,
    date: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string])
      .isRequired,
    editedAt: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    experienceDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    comment: PropTypes.string,
    ratings: PropTypes.shape({
      atmosphere: PropTypes.number,
      food: PropTypes.number,
      quality: PropTypes.number,
      service: PropTypes.number,
    }),
    reactionSummary: PropTypes.object,
    photoIds: PropTypes.arrayOf(PropTypes.string),
    photoRevision: PropTypes.number,
    photoSummary: PropTypes.object,
  }).isRequired,
  variant: PropTypes.oneOf(['full', 'profile']),
  breweryId: PropTypes.string,
  showBreweryReactionDock: PropTypes.bool,
  showNewChip: PropTypes.bool,
  analyticsSource: PropTypes.oneOf(['pint-portfolio', 'review-details']),
  itemType: PropTypes.oneOf(['beer', 'brewery']),
};

export default HospitalityReviewCard;
